import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, Clock, Edit, Loader2, MoreHorizontal, Search, UserX } from 'lucide-react';
import {
  LwAvatar,
  LwButton,
  LwConfirmDialog,
  LwFilterMenu,
  LwModal,
  LwPageChrome,
  LwSearchInput,
  LwSegmented,
  LwSortableHeader,
  LwStateChips,
  LwStatusPill,
  LwTable,
  LwThemeScope,
  type LwSortDirection,
  type LwStatusTone
} from '@/components/lw';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatPhoneNumber } from '@/utils/phoneFormatting';
import { obConnectionState } from './obConnectionState';
import type { User } from './types';
import {
  hasUserManagementFilters,
  type UserManagementFilterFacet,
  type UserManagementFilterOptions,
  type UserManagementFilters,
  type UserManagementStateMatchMode
} from './userManagementFilters';

export type UserManagementTab = 'active' | 'pending' | 'deactivated';
export type UserManagementSortDirection = 'asc' | 'desc' | null;

export interface UserManagementSortConfig {
  key: string;
  direction: UserManagementSortDirection;
}

export interface UserManagementCounts {
  active: number;
  pending: number;
  deactivated: number;
}

type UserActionResult = boolean | void | Promise<boolean | void>;

export interface UserManagementViewProps {
  users: readonly User[];
  visibleUsers: readonly User[];
  counts: UserManagementCounts;
  activeTab: UserManagementTab;
  onActiveTabChange: (tab: UserManagementTab) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  filters: UserManagementFilters;
  stateMatchMode: UserManagementStateMatchMode;
  onStateMatchModeChange: (mode: UserManagementStateMatchMode) => void;
  filterOptions: UserManagementFilterOptions;
  onFilterToggle: (facet: UserManagementFilterFacet, value: string) => void;
  onFilterClear: (facet: UserManagementFilterFacet) => void;
  onClearAllFilters: () => void;
  sortConfig: UserManagementSortConfig;
  onSort: (key: string) => void;
  loading: boolean;
  loadError?: string | null;
  onRetry: () => void;
  isAdmin: boolean;
  isPartnerMode: boolean;
  onEdit: (user: User) => void;
  onDeactivate: (user: User) => UserActionResult;
  onReactivate: (user: User) => UserActionResult;
  onApprove: (user: User, role: string) => UserActionResult;
  onReject: (user: User) => UserActionResult;
}

type ConfirmAction = 'deactivate' | 'reactivate' | 'reject';

interface ConfirmRequest {
  action: ConfirmAction;
  user: User;
  returnFocusTarget?: HTMLElement | null;
}

interface FocusCandidate {
  userId: string;
  label: string;
}

interface FocusRecoveryPlan {
  next?: FocusCandidate;
  previous?: FocusCandidate;
  outcome: string;
}

const APPROVAL_ROLES = [
  ['loan_officer', 'Loan Officer'],
  ['loan_officer_assistant', 'Loan Officer Assistant'],
  ['processor', 'Processor'],
  ['underwriter', 'Underwriter'],
  ['manager', 'Manager'],
  ['admin', 'Admin'],
  ['recruiter', 'Recruiter']
] as const;

const STATE_MATCH_ITEMS = [
  { value: 'any' as const, label: 'Any' },
  { value: 'all' as const, label: 'All' }
] as const;

const fullName = (user: User): string => {
  const name = `${user.firstName} ${user.lastName}`.trim();
  return name || user.email.split('@')[0] || 'Unnamed user';
};

const isIncomplete = (user: User): boolean =>
  !user.firstName ||
  !user.lastName ||
  user.firstName === 'Pending' ||
  user.lastName === 'User';

const statusPresentation = (user: User): { label: string; tone: LwStatusTone } => {
  if (user.status === 'Active') {
    return { label: 'Active', tone: 'success' };
  }
  if (user.status === 'Pending') {
    return { label: 'Approval pending', tone: 'warning' };
  }
  return { label: 'Deactivated', tone: 'neutral' };
};

const getSortDirection = (
  sortConfig: UserManagementSortConfig,
  key: string
): LwSortDirection => {
  if (sortConfig.key !== key || !sortConfig.direction) {
    return false;
  }
  return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
};

function IntegrationCell({ user }: { user: User }) {
  const obState = obConnectionState(user.optimalBlueUserId, user.optimalBlueBusinessChannelId);
  const encompassConnected = Boolean(user.encompassUserId);
  const optimalBlueConnected = obState === 'connected';
  const status = encompassConnected && optimalBlueConnected
    ? { label: 'Synced', attention: false }
    : encompassConnected
      ? { label: 'OB - Not synced', attention: true }
      : optimalBlueConnected
        ? { label: 'Enc - Not synced', attention: true }
        : { label: 'Not Synced', attention: false };
  const planName = user.optimalBlueCurrentScheduleName?.trim();
  const encompassDescription = user.encompassUserId
    ? `Encompass connected: ${user.encompassUserId}`
    : 'Encompass not connected';
  const obIdentifier = user.optimalBlueUsername || user.optimalBlueUserId;
  const obDescription = obState === 'connected'
    ? `Optimal Blue connected: ${obIdentifier}`
    : obState === 'partial'
      ? `Optimal Blue needs a business channel: ${obIdentifier}`
      : 'Optimal Blue not connected';

  return (
    <div className="lw-user-integrations">
      <div className="lw-user-integrations__chips">
        <span
          className="lw-user-integration-chip"
          data-state={user.encompassUserId ? 'connected' : 'off'}
          title={encompassDescription}
          aria-label={encompassDescription}
        >
          ENC <span aria-hidden="true" />
        </span>
        <span
          className="lw-user-integration-chip"
          data-state={obState === 'connected' ? 'connected' : obState === 'partial' ? 'attention' : 'off'}
          title={obDescription}
          aria-label={obDescription}
        >
          OB <span aria-hidden="true" />
        </span>
      </div>
      <span className={status.attention ? 'lw-user-plan is-attention' : 'lw-user-plan'}>{status.label}</span>
      {encompassConnected && optimalBlueConnected && planName && (
        <span className="lw-user-plan-detail">Plan: {planName}</span>
      )}
    </div>
  );
}

function ContactCell({ user }: { user: User }) {
  const formatContactPhone = (value: string) => formatPhoneNumber(value).replace(')-', ') ');
  const phoneSummary = [
    user.phone
      ? `${formatContactPhone(user.phone)}${user.officeExtension ? ` ×${user.officeExtension}` : ''}`
      : '',
    user.mobilePhone ? formatContactPhone(user.mobilePhone) : ''
  ].filter(Boolean).join(' · ');

  return (
    <div className="lw-user-contact">
      <span>{user.email}</span>
      {phoneSummary && (
        <span className="lw-user-contact__phones" title={phoneSummary}>{phoneSummary}</span>
      )}
    </div>
  );
}

interface RowActionsProps {
  user: User;
  activeTab: UserManagementTab;
  isAdmin: boolean;
  onEdit: (user: User) => void;
  onConfirmRequest: (request: ConfirmRequest) => void;
  onApprovalRequest: (user: User) => void;
  onActionTriggerChange: (userId: string, trigger: HTMLButtonElement | null) => void;
}

function RowActions({
  user,
  activeTab,
  isAdmin,
  onEdit,
  onConfirmRequest,
  onApprovalRequest,
  onActionTriggerChange
}: RowActionsProps) {
  const name = fullName(user);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const setMenuTrigger = useCallback((trigger: HTMLButtonElement | null) => {
    menuTriggerRef.current = trigger;
    onActionTriggerChange(user.id, trigger);
  }, [onActionTriggerChange, user.id]);
  if (activeTab === 'pending' && isAdmin) {
    return (
      <div className="lw-user-pending-actions">
        <LwButton variant="sm-solid" onClick={() => onApprovalRequest(user)}>
          <Check aria-hidden="true" /> Approve
        </LwButton>
        <LwButton
          variant="sm-danger"
          onClick={(event) => onConfirmRequest({
            action: 'reject',
            user,
            returnFocusTarget: event.currentTarget
          })}
        >
          Reject
        </LwButton>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          ref={setMenuTrigger}
          className="lw-user-action-trigger"
          type="button"
          aria-label={`Actions for ${name}`}
          onClick={(event) => event.stopPropagation()}
        >
          <MoreHorizontal aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="lw-ds" align="end">
        <DropdownMenuItem onSelect={() => onEdit(user)}>
          <Edit aria-hidden="true" /> Edit
        </DropdownMenuItem>
        {user.status === 'Active' && (
          <DropdownMenuItem onSelect={() => onConfirmRequest({
            action: 'deactivate',
            user,
            returnFocusTarget: menuTriggerRef.current
          })}>
            <UserX aria-hidden="true" /> Deactivate
          </DropdownMenuItem>
        )}
        {user.status === 'Terminated' && (
          <DropdownMenuItem onSelect={() => onConfirmRequest({
            action: 'reactivate',
            user,
            returnFocusTarget: menuTriggerRef.current
          })}>
            <Check aria-hidden="true" /> Reactivate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserPhoto({ user }: { user: User }) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const photoSource = user.profilePicture?.trim() || '';
  if (photoSource && failedSource !== photoSource) {
    return (
      <img
        src={photoSource}
        alt=""
        loading="lazy"
        onError={() => setFailedSource(photoSource)}
      />
    );
  }
  return <LwAvatar first={user.firstName || user.email} last={user.lastName} />;
}

function UserIdentity({
  user,
  onEdit,
  showTitle = false
}: {
  user: User;
  onEdit?: (user: User) => void;
  showTitle?: boolean;
}) {
  const name = fullName(user);
  return (
    <div className="lw-user-identity">
      <UserPhoto user={user} />
      <div className="lw-user-identity__copy">
        <div className="lw-user-identity__name">
          {onEdit ? (
            <button className="lw-user-name" type="button" onClick={() => onEdit(user)}>{name}</button>
          ) : (
            <span className="lw-user-name">{name}</span>
          )}
          {isIncomplete(user) && (
            <LwStatusPill label="Incomplete profile" tone="warning" className="lw-user-incomplete" />
          )}
        </div>
        {showTitle && user.title?.trim() && (
          <span className="lw-user-title">{user.title.trim()}</span>
        )}
      </div>
    </div>
  );
}

const roleTone = (role: string): 'admin' | 'branch-manager' | 'neutral' => {
  const normalized = role.trim().toLowerCase();
  if (normalized === 'admin') {
    return 'admin';
  }
  if (normalized === 'branch manager') {
    return 'branch-manager';
  }
  return 'neutral';
};

function RoleChips({ user }: { user: User }) {
  return (
    <div className="lw-user-role-chips">
      {(user.roles?.length ? user.roles : ['No Role']).map((role) => (
        <span key={role} data-role-tone={roleTone(role)}>{role}</span>
      ))}
    </div>
  );
}

function MobileUserCard({
  user,
  activeTab,
  isAdmin,
  onEdit,
  onConfirmRequest,
  onApprovalRequest,
  onActionTriggerChange,
  waitingLabel
}: RowActionsProps & { waitingLabel: string }) {
  const status = statusPresentation(user);
  return (
    <article
      className="lw-user-card"
      data-status={user.status.toLowerCase()}
      onClick={(event) => {
        if (
          !(event.target instanceof Element) ||
          !event.target.closest('button, a, input, select, textarea, [role="button"], [role="menuitem"], [role="option"]')
        ) {
          onEdit(user);
        }
      }}
    >
      <button
        className="lw-user-card__edit-control"
        type="button"
        aria-label={`Edit ${fullName(user)}`}
        onClick={() => onEdit(user)}
      />
      <div className="lw-user-card__head">
        <UserIdentity user={user} />
        <RowActions
          user={user}
          activeTab={activeTab}
          isAdmin={isAdmin}
          onEdit={onEdit}
          onConfirmRequest={onConfirmRequest}
          onApprovalRequest={onApprovalRequest}
          onActionTriggerChange={onActionTriggerChange}
        />
      </div>
      <ContactCell user={user} />
      <dl>
        <div><dt>Roles</dt><dd><RoleChips user={user} /></dd></div>
        <div><dt>NMLS</dt><dd className="tabular-nums">{user.nmls || 'Not provided'}</dd></div>
        <div>
          <dt>States</dt>
          <dd>
            {user.licensedStates.length > 0
              ? <LwStateChips states={user.licensedStates.map((code) => ({ code }))} />
              : <span className="lw-user-muted">None</span>}
          </dd>
        </div>
        <div><dt>Integrations</dt><dd><IntegrationCell user={user} /></dd></div>
        <div>
          <dt>Status</dt>
          <dd>
            {user.status === 'Pending' && isAdmin
              ? <span className="lw-user-waiting"><Clock aria-hidden="true" /> Waiting {waitingLabel}</span>
              : <LwStatusPill label={status.label} tone={status.tone} />}
          </dd>
        </div>
      </dl>
    </article>
  );
}

const confirmCopy = (request: ConfirmRequest | null) => {
  if (!request) {
    return null;
  }
  const name = fullName(request.user);
  switch (request.action) {
    case 'deactivate':
      return {
        title: `Deactivate ${name}?`,
        description: 'Reversible — reactivate this user any time from the Deactivated view.',
        confirmLabel: 'Deactivate',
        destructive: false,
        consequences: [
          'The profile is marked deactivated in Lendwise.',
          'Loans, records, and history remain available for reassignment.',
          'Encompass access is disabled first when this profile is linked.'
        ]
      };
    case 'reactivate':
      return {
        title: `Reactivate ${name}?`,
        description: 'This restores their Lendwise employment status and attempts to restore Encompass access.',
        confirmLabel: 'Reactivate',
        destructive: false,
        consequences: [
          'The user can sign in again after the status update completes.',
          'Existing records and assignments remain attached to this profile.'
        ]
      };
    case 'reject':
      return {
        title: `Reject ${name}?`,
        description: 'The pending registration will be terminated and its requested roles removed.',
        confirmLabel: 'Reject User',
        destructive: true,
        consequences: [
          'The applicant will not receive portal access.',
          'An administrator can review the deactivated record later.'
        ]
      };
  }
};

export function UserManagementView({
  visibleUsers,
  counts,
  activeTab,
  onActiveTabChange,
  searchTerm,
  onSearchTermChange,
  filters,
  stateMatchMode,
  onStateMatchModeChange,
  filterOptions,
  onFilterToggle,
  onFilterClear,
  onClearAllFilters,
  sortConfig,
  onSort,
  loading,
  loadError,
  onRetry,
  isAdmin,
  isPartnerMode,
  onEdit,
  onDeactivate,
  onReactivate,
  onApprove,
  onReject
}: UserManagementViewProps) {
  const isMobile = useIsMobile();
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);
  const [approvalTarget, setApprovalTarget] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState('loan_officer');
  const [submitting, setSubmitting] = useState(false);
  const [focusRecovery, setFocusRecovery] = useState<FocusRecoveryPlan | null>(null);
  const [actionAnnouncement, setActionAnnouncement] = useState('');
  const confirmationInFlightRef = useRef(false);
  const actionTriggerRefs = useRef(new Map<string, HTMLButtonElement>());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const copy = confirmCopy(confirmRequest);
  const hasFilters = hasUserManagementFilters(filters);
  const stateFilterTriggerLabel = filters.states.length === 0
    ? undefined
    : stateMatchMode === 'all'
      ? `State: ${filters.states.join(' + ')} (all)`
      : `State: ${filters.states.join(', ')}`;
  const activeTabTotal = counts[activeTab];
  const activePanelId = `manage-users-${activeTab}-panel`;
  const activeTabId = `manage-users-${activeTab}-tab`;
  const tabItems = useMemo(() => {
    const items = [
      { value: 'active' as const, label: 'Active', count: counts.active, panelId: 'manage-users-active-panel', tabId: 'manage-users-active-tab' },
      { value: 'pending' as const, label: 'Pending', count: counts.pending, panelId: 'manage-users-pending-panel', tabId: 'manage-users-pending-tab' },
      { value: 'deactivated' as const, label: 'Deactivated', count: counts.deactivated, panelId: 'manage-users-deactivated-panel', tabId: 'manage-users-deactivated-tab' }
    ];
    return isPartnerMode ? items.filter((item) => item.value !== 'pending') : items;
  }, [counts.active, counts.deactivated, counts.pending, isPartnerMode]);

  const registerActionTrigger = useCallback((userId: string, trigger: HTMLButtonElement | null) => {
    if (trigger) {
      actionTriggerRefs.current.set(userId, trigger);
    } else {
      actionTriggerRefs.current.delete(userId);
    }
  }, []);

  useLayoutEffect(() => {
    if (!focusRecovery || confirmRequest) {
      return;
    }

    const candidates = [focusRecovery.next, focusRecovery.previous].filter(
      (candidate): candidate is FocusCandidate => Boolean(candidate)
    );
    let target: HTMLElement | null = null;
    let targetLabel = 'Search users';
    for (const candidate of candidates) {
      const candidateTarget = actionTriggerRefs.current.get(candidate.userId);
      if (candidateTarget?.isConnected) {
        target = candidateTarget;
        targetLabel = `Actions for ${candidate.label}`;
        break;
      }
    }
    if (!target && searchInputRef.current?.isConnected) {
      target = searchInputRef.current;
    }
    target?.focus();
    setActionAnnouncement(`${focusRecovery.outcome} Focus moved to ${targetLabel}.`);
    setFocusRecovery(null);
  }, [confirmRequest, focusRecovery]);

  const waitingLabel = (user: User) => {
    const created = new Date(user.createdAt);
    const hours = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60));
    if (hours < 1) {
      return 'less than 1 hour';
    }
    if (hours === 1) {
      return '1 hour';
    }
    if (hours < 24) {
      return `${hours} hours`;
    }
    const days = Math.floor(hours / 24);
    return days === 1 ? '1 day' : `${days} days`;
  };

  const executeConfirmation = async () => {
    if (!confirmRequest) {
      return;
    }
    const rowIndex = visibleUsers.findIndex((user) => user.id === confirmRequest.user.id);
    const recoveryPlan = confirmRequest.action === 'deactivate'
      ? {
          next: rowIndex >= 0 && visibleUsers[rowIndex + 1]
            ? {
                userId: visibleUsers[rowIndex + 1].id,
                label: fullName(visibleUsers[rowIndex + 1])
              }
            : undefined,
          previous: rowIndex > 0
            ? {
                userId: visibleUsers[rowIndex - 1].id,
                label: fullName(visibleUsers[rowIndex - 1])
              }
            : undefined,
          outcome: `${fullName(confirmRequest.user)} was deactivated.`
        }
      : null;
    confirmationInFlightRef.current = true;
    setSubmitting(true);
    try {
      let result: boolean | void;
      if (confirmRequest.action === 'deactivate') {
        result = await onDeactivate(confirmRequest.user);
      } else if (confirmRequest.action === 'reactivate') {
        result = await onReactivate(confirmRequest.user);
      } else {
        result = await onReject(confirmRequest.user);
      }
      if (result !== false) {
        if (recoveryPlan) {
          setFocusRecovery(recoveryPlan);
        }
        setConfirmRequest(null);
      }
    } finally {
      confirmationInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const executeApproval = async () => {
    if (!approvalTarget) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await onApprove(approvalTarget, selectedRole);
      if (result !== false) {
        setApprovalTarget(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requestConfirmation = (request: ConfirmRequest) => {
    setActionAnnouncement('');
    setConfirmRequest(request);
  };

  const renderTableBody = () => (
    <tbody>
      {visibleUsers.map((user) => {
        const status = statusPresentation(user);
        return (
          <tr
            key={user.id}
            data-status={user.status.toLowerCase()}
            tabIndex={0}
            onClick={(event) => {
              if (
                !(event.target instanceof Element) ||
                !event.target.closest('button, a, input, select, textarea, [role="button"], [role="menuitem"], [role="option"]')
              ) {
                onEdit(user);
              }
            }}
            onKeyDown={(event) => {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onEdit(user);
              }
            }}
          >
            <td className="lw-user-cell"><UserIdentity user={user} onEdit={onEdit} showTitle /></td>
            <td><ContactCell user={user} /></td>
            <td><RoleChips user={user} /></td>
            <td className="tabular-nums">{user.nmls || '—'}</td>
            <td>
              {user.licensedStates.length > 0
                ? <LwStateChips states={user.licensedStates.map((code) => ({ code }))} />
                : <span className="lw-user-muted">—</span>}
            </td>
            <td><IntegrationCell user={user} /></td>
            <td>
              {user.status === 'Pending' && isAdmin
                ? <span className="lw-user-waiting"><Clock aria-hidden="true" /> Waiting {waitingLabel(user)}</span>
                : <LwStatusPill label={status.label} tone={status.tone} />}
            </td>
            <td className="lw-user-actions-cell">
              <RowActions
                user={user}
                activeTab={activeTab}
                isAdmin={isAdmin}
                onEdit={onEdit}
                onConfirmRequest={requestConfirmation}
                onApprovalRequest={setApprovalTarget}
                onActionTriggerChange={registerActionTrigger}
              />
            </td>
          </tr>
        );
      })}
    </tbody>
  );

  return (
    <LwThemeScope className="lw-ds-page lw-manage-users">
      <div className="wrap">
        <LwPageChrome
          breadcrumbs={[{ label: 'Company Settings' }, { label: 'Manage Users' }]}
          eyebrow={isPartnerMode ? 'Partner administration' : 'Company settings'}
          title={<>Manage <em>Users</em></>}
          description="Access, roles, licensing and integrations for everyone at LendWise."
        />

        <div className="lw-user-controls" role="group" aria-label="Manage user list">
          <div className="lw-user-tabs">
            <LwSegmented
              ariaLabel="User status"
              value={activeTab}
              items={tabItems}
              onValueChange={onActiveTabChange}
            />
          </div>
          <div className="lw-user-search">
            <LwSearchInput
              ref={searchInputRef}
              value={searchTerm}
              onChange={onSearchTermChange}
              placeholder="Search name, email, role, state…"
              ariaLabel="Search users"
            />
          </div>
          <span className="lw-user-filter-label">Filter</span>
          <div className="lw-user-filter-menus">
            <LwFilterMenu
              label="Role"
              icon={null}
              options={filterOptions.roles}
              selected={filters.roles}
              onToggle={(value) => onFilterToggle('roles', value)}
              onClear={() => onFilterClear('roles')}
              searchable
            />
            <LwFilterMenu
              label="State"
              icon={null}
              options={filterOptions.states}
              selected={filters.states}
              onToggle={(value) => onFilterToggle('states', value)}
              onClear={() => onFilterClear('states')}
              triggerLabel={stateFilterTriggerLabel}
              topContent={(
                <div className="lw-user-state-match">
                  <span>Match:</span>
                  <LwSegmented
                    mode="single-select"
                    ariaLabel="State match mode"
                    value={stateMatchMode}
                    items={STATE_MATCH_ITEMS}
                    onValueChange={onStateMatchModeChange}
                  />
                </div>
              )}
              searchable
            />
            <LwFilterMenu
              label="Integration"
              icon={null}
              options={filterOptions.integrations}
              selected={filters.integrations}
              onToggle={(value) => onFilterToggle('integrations', value)}
              onClear={() => onFilterClear('integrations')}
            />
          </div>
          <span className="lw-user-results" aria-live="polite">
            Showing <strong>{visibleUsers.length}</strong> of <strong>{activeTabTotal}</strong>
          </span>
        </div>

        <section
          id={activePanelId}
          role="tabpanel"
          aria-labelledby={activeTabId}
          tabIndex={0}
          className="lw-user-table"
        >
          <LwTable
            presentation="canonical"
            density="comfortable"
            ariaLabel={`${activeTab === 'deactivated' ? 'Deactivated' : activeTab[0].toUpperCase() + activeTab.slice(1)} users`}
          >
            {!isMobile && (
              <>
                <thead>
                  <tr>
                    <LwSortableHeader label="User" direction={getSortDirection(sortConfig, 'name')} onSort={() => onSort('name')} />
                    <LwSortableHeader label="Contact" direction={getSortDirection(sortConfig, 'email')} onSort={() => onSort('email')} />
                    <LwSortableHeader label="Roles" direction={getSortDirection(sortConfig, 'role')} onSort={() => onSort('role')} />
                    <LwSortableHeader label="NMLS" direction={getSortDirection(sortConfig, 'nmls')} onSort={() => onSort('nmls')} />
                    <LwSortableHeader label="Licensed States" direction={getSortDirection(sortConfig, 'states')} onSort={() => onSort('states')} />
                    <th scope="col">Integrations</th>
                    <LwSortableHeader label="Status" direction={getSortDirection(sortConfig, 'status')} onSort={() => onSort('status')} />
                    <th scope="col" className="is-right">Actions</th>
                  </tr>
                </thead>
                {!loading && !loadError && visibleUsers.length > 0 && renderTableBody()}
              </>
            )}
          </LwTable>

          {loading && (
            <div className="lw-user-state" role="status" aria-live="polite">
              <Loader2 className="animate-spin" aria-hidden="true" />
              <h2>Loading users</h2>
              <p>Retrieving roles, licenses, and integration status.</p>
            </div>
          )}
          {!loading && loadError && (
            <div className="lw-user-state is-error" role="alert">
              <AlertStateIcon />
              <h2>Users could not be loaded</h2>
              <p>{loadError}</p>
              <LwButton variant="sm" onClick={onRetry}>Try again</LwButton>
            </div>
          )}
          {!loading && !loadError && visibleUsers.length === 0 && (
            <div className="lw-user-state">
              <Search aria-hidden="true" />
              <h2>{searchTerm || hasFilters ? 'No users match' : `No ${activeTab} users`}</h2>
              <p>
                {searchTerm || hasFilters
                  ? 'No users match the current search and filters.'
                  : 'This view is clear.'}
              </p>
              {(searchTerm || hasFilters) && (
                <LwButton
                  variant="sm"
                  onClick={() => {
                    onSearchTermChange('');
                    onClearAllFilters();
                  }}
                >
                  {searchTerm && hasFilters
                    ? 'Clear search and filters'
                    : hasFilters
                      ? 'Clear filters'
                      : 'Clear search'}
                </LwButton>
              )}
            </div>
          )}
          {!loading && !loadError && visibleUsers.length > 0 && isMobile && (
            <div className="lw-user-card-list">
              {visibleUsers.map((user) => (
                <MobileUserCard
                  key={user.id}
                  user={user}
                  activeTab={activeTab}
                  isAdmin={isAdmin}
                  onEdit={onEdit}
                  onConfirmRequest={requestConfirmation}
                  onApprovalRequest={setApprovalTarget}
                  onActionTriggerChange={registerActionTrigger}
                  waitingLabel={waitingLabel(user)}
                />
              ))}
            </div>
          )}
          {!loading && !loadError && (
            <footer className="lw-user-footer">
              <span>
                Showing <strong>{visibleUsers.length}</strong> of <strong>{activeTabTotal}</strong> users
              </span>
              <span className="lw-user-legend">
                <span><i data-tone="connected" aria-hidden="true" />connected</span>
                <span aria-hidden="true">·</span>
                <span><i data-tone="attention" aria-hidden="true" />needs attention</span>
                <span aria-hidden="true">·</span>
                <span><i data-tone="off" aria-hidden="true" />not set up</span>
                <span aria-hidden="true">·</span>
                <span>click a row to open the profile</span>
              </span>
            </footer>
          )}
        </section>
      </div>

      <div className="sr-only lw-user-action-announcement" role="status" aria-live="polite" aria-atomic="true">
        {actionAnnouncement}
      </div>

      {copy && confirmRequest && (
        <LwConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !submitting && !confirmationInFlightRef.current) {
              setConfirmRequest(null);
            }
          }}
          title={copy.title}
          description={copy.description}
          confirmLabel={submitting ? 'Working…' : copy.confirmLabel}
          onConfirm={() => {
            void executeConfirmation();
          }}
          destructive={copy.destructive}
          disabled={submitting}
          returnFocusTo={confirmRequest.returnFocusTarget}
        >
          <ul className="lw-user-consequences">
            {copy.consequences.map((consequence) => <li key={consequence}>{consequence}</li>)}
          </ul>
        </LwConfirmDialog>
      )}

      {approvalTarget && (
        <LwModal
          eyebrow={<span className="lw-user-modal-eyebrow">Pending registration</span>}
          title="Approve User"
          sub={`Approve ${fullName(approvalTarget)} (${approvalTarget.email}) and assign a role.`}
          onClose={() => {
            if (!submitting) {
              setApprovalTarget(null);
            }
          }}
          footer={(
            <>
              <LwButton variant="sm" onClick={() => setApprovalTarget(null)} disabled={submitting}>Cancel</LwButton>
              <LwButton variant="sm-solid" onClick={() => void executeApproval()} disabled={submitting}>
                {submitting ? <><Loader2 className="animate-spin" aria-hidden="true" /> Approving…</> : `Approve as ${APPROVAL_ROLES.find(([value]) => value === selectedRole)?.[1] ?? 'selected role'}`}
              </LwButton>
            </>
          )}
        >
          <label className="field lw-user-approval-field">
            <span>Approve as</span>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger aria-label="Approve as">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent className="lw-ds">
                {APPROVAL_ROLES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <div className="lw-user-approval-note">
            <Clock aria-hidden="true" />
            Waiting {waitingLabel(approvalTarget)}. Access is granted after the role and organization update completes.
          </div>
        </LwModal>
      )}
    </LwThemeScope>
  );
}

function AlertStateIcon() {
  return <UserX aria-hidden="true" />;
}
